import clsx from "clsx";
import { isString } from "lodash-es";
import { useCallback, useEffect, useMemo, useState } from "react";
import ReactFocusLock from "react-focus-lock";
import { type HtmlPortalNode, InPortal } from "react-reverse-portal";

import { useGame } from "~/components/GameContext";
import { useLocale } from "~/hooks/locale";

import { STREAK_REQ_FOR_HINT } from "../const";
import type { GameWord } from "../Game";
import type { GameAnswer } from "../utils/answer";
import { getBestFit, getMatches } from "../utils/match";
import { AccentShortcuts } from "./AccentShortcuts";

export const Classic: React.FC<{
  word: GameWord;
  hintPoints: number;
  portalNode: HtmlPortalNode;
  isFirstLetterShown: boolean;
  isSoundEnabled: boolean;
  isDisabled: boolean;
  onAnswer: React.Dispatch<GameAnswer>;
  onHintUsed: () => void;
}> = ({
  word,
  onAnswer,
  portalNode,
  isFirstLetterShown,
  isSoundEnabled,
  isDisabled,
  hintPoints,
  onHintUsed,
}) => {
  const { t } = useLocale();
  const { game, playSound } = useGame();
  const [value, setValue] = useState("");
  const [caret, setCaret] = useState(-1);

  const updateCaret = useCallback<React.FormEventHandler<HTMLInputElement>>(
    (event) => {
      const start = event.currentTarget.selectionStart ?? 0;
      const end = event.currentTarget.selectionEnd ?? start;
      setCaret(
        start === end // if the start and end are the same, there's no selection
          ? start - 1 // target the character before the caret
          : end - start === 1 // there is a selection of one character
            ? start // target the start of the selection
            : -1, // unset the caret if there's a selection of more than one character
      );
    },
    [],
  );

  const bestFit = useMemo(
    () =>
      getBestFit(
        value,
        word.solution,
        word.alternates.map((alternate) => alternate.text),
        game.settings.strictness,
      ),
    [word, value, game.settings],
  );

  const firstLetter = useMemo(() => bestFit.charAt(0).toUpperCase(), [bestFit]);

  const matches = useMemo(
    () => getMatches(value, bestFit, game.settings.strictness),
    [bestFit, value, game.settings.strictness],
  );

  const allMatches = useMemo(() => matches.flat(), [matches]);
  const isCorrect = useMemo(
    () => allMatches.every((match) => match === true),
    [allMatches],
  );

  const hasHint = useMemo(
    () => hintPoints >= STREAK_REQ_FOR_HINT,
    [hintPoints],
  );

  const advanceWord = useCallback(
    (correct: boolean) => {
      onAnswer({
        word,
        solution: bestFit,
        correct,
      });

      if (isSoundEnabled) {
        playSound(
          correct
            ? hintPoints === STREAK_REQ_FOR_HINT - 1
              ? "earn_hint"
              : "correct"
            : "skip",
        );
      }
    },
    [bestFit, onAnswer, word, playSound, isSoundEnabled, hintPoints],
  );

  const pass = useCallback(() => {
    if (hasHint) {
      const hint = allMatches.find(isString);
      setValue((prev) => prev + hint);
      onHintUsed();

      return;
    }

    advanceWord(false);
  }, [advanceWord, allMatches, hasHint, onHintUsed]);

  useEffect(() => {
    if (isCorrect) {
      advanceWord(true);
    }
  }, [bestFit, isCorrect, advanceWord]);

  return (
    <>
      <InPortal node={portalNode}>
        {/* this injects the skip/hint button into the game layout */}
        <button
          className={clsx(
            "btn lg:btn-lg",
            hasHint
              ? "btn-primary bg-gradient-to-b from-international-orange-400"
              : "btn-secondary",
          )}
          onClick={pass}
          onMouseDown={(event) => {
            event.preventDefault();
          }}
        >
          <span className="kbd kbd-xs lg:kbd-sm max-md:hidden">esc</span>
          {hasHint ? t("hint") : t("skip")}
        </button>
      </InPortal>
      {game.settings.isTypingFeedbackShown && (
        <div className="absolute bottom-1/4 left-1/2 flex w-2/3 flex-wrap justify-center gap-x-3 gap-y-2 -translate-x-1/2 translate-y-1/2">
          {matches.map((match, index) => (
            <div key={index} className="flex gap-1">
              {match.map((response, index) => (
                <div
                  key={index}
                  className={clsx(
                    "size-3 border-2 border-current transition-colors",
                    isString(response)
                      ? "text-base-content"
                      : clsx(
                          "bg-current",
                          response ? "text-accent" : "text-error",
                        ),
                  )}
                />
              ))}
            </div>
          ))}
        </div>
      )}
      <ReactFocusLock autoFocus persistentFocus disabled={isDisabled}>
        <input
          type="search"
          aria-autocomplete="both"
          aria-haspopup={false}
          autoCapitalize="off"
          autoComplete="off"
          autoCorrect="off"
          spellCheck={false}
          className={clsx(
            // -inset-px so the red border overlaps the outer border
            "absolute -inset-px border-2 bg-transparent text-center outline-none transition-colors title title-xl selection:bg-base-content selection:text-base-100 placeholder:text-base-300 max-sm:inset-0 max-sm:-top-px [&::-webkit-search-cancel-button]:hidden",
            allMatches.includes(false) ? "border-error" : "border-transparent",
          )}
          placeholder={
            isFirstLetterShown
              ? t("starts_with", { letter: firstLetter })
              : t("type_answer")
          }
          value={value}
          onKeyDown={(event) => {
            if (event.key === "Escape" && !event.repeat) {
              event.preventDefault();
              pass();
            }
          }}
          onChange={(event) => {
            setValue(
              event.target.value.replace(/^\s+/, "").replace(/\s+/g, " "),
            );
          }}
          onInput={updateCaret}
          onSelect={updateCaret}
        />
      </ReactFocusLock>
      {game.settings.strictness.acc && (
        <AccentShortcuts
          onSelect={(letter) => {
            setValue(
              (prev) => prev.slice(0, caret) + letter + prev.slice(caret + 1),
            );
          }}
        >
          {value.charAt(caret)}
        </AccentShortcuts>
      )}
    </>
  );
};
